import isEmpty from 'lodash/isEmpty';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { EmbeddedMessage } from 'constants/messageMediums';
import { spacing } from 'ui/theme';
import { LongArrowRight, MakeCopy, Edit16 } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import { useCampaignContext, useEditorContext, useUnsavedPromptContext } from '../../context';
import { useSampleApiRequest, useSaveCampaign } from '../../hooks';
import { CampaignEditorFormState, CampaignStep } from '../../types';

const ButtonsContainer = styled.div`
  display: flex;
  gap: ${spacing.fiveEights};
`;

type CommonButtonProps = {
  campaignStep: CampaignStep;
};

type SharedEditActionButtonProps = {
  children: React.ReactNode;
  editAllowed: boolean;
  onClick: () => any;
  dataTest: string;
};

export const COPY_SAMPLE_REQUEST_BUTTON_TEST_ID = 'copy-sample-request-button';
export const EDIT_CAMPAIGN_STEP_BUTTON = 'edit-button';
export const CANCEL_CAMPAIGN_STEP_BUTTON = 'cancel-button';
export const SAVE_CAMPAIGN_STEP_BUTTON = 'save-button';
export const AUDIENCE_SELECTION_BUTTON = 'audience-selection-button';
export const AUDIENCE_ELIGIBILITY_BUTTON = 'audience-eligiblity-selection-button';

const AudienceTriggeredButton = ({ campaignStep }: CommonButtonProps) => {
  const { copySampleApiRequest } = useSampleApiRequest();
  const { viewCampaignStep } = useEditorContext();
  const { transitionTo } = useUnsavedPromptContext();
  const handleCopy = () => {
    transitionTo(() => viewCampaignStep(campaignStep));
    copySampleApiRequest();
  };

  return (
    <Button
      data-test={COPY_SAMPLE_REQUEST_BUTTON_TEST_ID}
      size={BUTTON_SIZES.SMALL}
      variant={BUTTON_VARIANTS.SECONDARY}
      onClick={handleCopy}
    >
      <MakeCopy height={20} width={20} />
      Copy sample request
    </Button>
  );
};

const EditableCampaignStepButtons = ({ campaignStep }: CommonButtonProps) => {
  const { saveCampaign, isLoading } = useSaveCampaign();
  const { viewCampaignStep } = useEditorContext();
  const { refetch, isLoading: isCampaignLoading } = useCampaignContext();
  const { transitionToCampaignStep } = useUnsavedPromptContext();
  const {
    getValues,
    formState: { errors, isDirty },
  } = useFormContext();
  const savingAndLoading = isCampaignLoading || isLoading;
  const isDisabled = !isEmpty(errors) || !isDirty;
  return (
    <ButtonsContainer>
      <Button
        isDisabled={savingAndLoading}
        data-test={CANCEL_CAMPAIGN_STEP_BUTTON}
        size={BUTTON_SIZES.SMALL}
        variant={BUTTON_VARIANTS.SECONDARY}
        onClick={() => transitionToCampaignStep(campaignStep, () => viewCampaignStep(campaignStep))}
      >
        Cancel
      </Button>
      <Button
        isLoading={savingAndLoading}
        type="submit"
        data-test={SAVE_CAMPAIGN_STEP_BUTTON}
        size={BUTTON_SIZES.SMALL}
        variant={BUTTON_VARIANTS.PRIMARY}
        isDisabled={isDisabled}
        onClick={() =>
          saveCampaign(getValues() as CampaignEditorFormState, () => {
            refetch();
            viewCampaignStep(campaignStep);
          })
        }
      >
        Save
      </Button>
    </ButtonsContainer>
  );
};

const SharedEditActionButton = ({
  children,
  editAllowed,
  onClick,
  dataTest,
}: SharedEditActionButtonProps) => {
  return (
    <>
      <Tooltip
        isEnabled={!editAllowed}
        tooltipContent={'You do not have permission to edit campaigns'}
      >
        <Button
          isDisabled={!editAllowed}
          data-test={dataTest}
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.SECONDARY}
          onClick={onClick}
        >
          {children}
        </Button>
      </Tooltip>
    </>
  );
};

const AudienceButton = () => {
  const { setIsAudienceTakeoverOpen, canDraftMessaging } = useEditorContext();
  const { transitionTo } = useUnsavedPromptContext();
  const { campaign } = useCampaignContext();
  const sendLists = campaign?.lists || [];
  return (
    <SharedEditActionButton
      editAllowed={canDraftMessaging}
      onClick={() => transitionTo(() => setIsAudienceTakeoverOpen(true))}
      dataTest={AUDIENCE_SELECTION_BUTTON}
    >
      {sendLists.length === 0 ? (
        <>
          Select lists <LongArrowRight height={16} width={16} />
        </>
      ) : (
        <>
          <Edit16 />
          Edit
        </>
      )}
    </SharedEditActionButton>
  );
};

const AudienceEligibilityButtons = () => {
  const { transitionTo } = useUnsavedPromptContext();
  const { canDraftMessaging, setIsAudienceTakeoverOpen } = useEditorContext();
  const { campaign } = useCampaignContext();
  const eligibilityLists = campaign?.lists || [];
  return (
    <ButtonsContainer>
      <SharedEditActionButton
        editAllowed={canDraftMessaging}
        onClick={() => transitionTo(() => setIsAudienceTakeoverOpen(true))}
        dataTest={AUDIENCE_ELIGIBILITY_BUTTON}
      >
        {eligibilityLists.length > 0 ? (
          <>
            <Edit16 />
            Edit
          </>
        ) : (
          <>
            Select list <LongArrowRight height={16} width={16} />
          </>
        )}
      </SharedEditActionButton>
    </ButtonsContainer>
  );
};

export const CampaignCardActions = ({ campaignStep }: CommonButtonProps) => {
  const { editCampaignStep, currentEditableStep, canDraftMessaging } = useEditorContext();
  const { campaign } = useCampaignContext();

  const { transitionTo } = useUnsavedPromptContext();

  if (campaignStep === CampaignStep.Display) return null;

  if (campaignStep === CampaignStep.AudienceTriggered) {
    // This step is used for workflow triggered embedded campaigns and we want to remove the copy request button
    if (campaign?.messageMedium === EmbeddedMessage) return null;
    return <AudienceTriggeredButton campaignStep={campaignStep} />;
  }

  if (campaignStep === CampaignStep.Audience) return <AudienceButton />;

  if (campaignStep === CampaignStep.AudienceEligibility) return <AudienceEligibilityButtons />;

  if (currentEditableStep === campaignStep)
    return <EditableCampaignStepButtons campaignStep={campaignStep} />;

  return (
    <Tooltip
      isEnabled={!canDraftMessaging}
      tooltipContent={'You do not have permission to edit campaigns'}
    >
      <Button
        isDisabled={!canDraftMessaging}
        data-test={EDIT_CAMPAIGN_STEP_BUTTON}
        size={BUTTON_SIZES.SMALL}
        variant={BUTTON_VARIANTS.SECONDARY}
        onClick={() => transitionTo(() => editCampaignStep(campaignStep))}
      >
        <Edit16 />
        Edit
      </Button>
    </Tooltip>
  );
};
